<script setup>
import { reactive, ref } from "vue";
import { GqlService } from "@js/services/gql";
import FileUploader from "@js/vue/components/FileUploader.vue";

const props = defineProps({
	policyUid: {
		type: String,
		required: true,
	},
	currencySymbol: {
		type: String,
		required: true,
	},
	backUrl: {
		type: String,
		default: "",
	},
});

const formData = reactive({
	replacementValue: "",
	damageValue: "",
	damageDescription: "",
	uploadedFiles: [],
});

const formRef = ref(null);
const isSubmitting = ref(false);
const errors = ref({});
const generalError = ref("");

/**
 * Validates the form data
 */
const validateForm = () => {
	const newErrors = {};

	// Clear general error when validating
	generalError.value = "";

	// Replacement value is optional, but if provided must be greater than 0
	if (formData.replacementValue && formData.replacementValue <= 0) {
		newErrors.replacementValue =
			"Replacement value must be greater than 0 if provided.";
	}

	if (!formData.damageValue || formData.damageValue < 0) {
		newErrors.damageValue =
			"Damage value is required and must be 0 or greater.";
	}

	if (!formData.damageDescription?.trim()) {
		newErrors.damageDescription = "Damage description is required.";
	}

	errors.value = newErrors;
	return Object.keys(newErrors).length === 0;
};

/**
 * Handles form submission
 */
const handleSubmit = async (event) => {
	event.preventDefault();

	if (!validateForm()) {
		return;
	}

	isSubmitting.value = true;

	try {
		const json = await GqlService.request(
			`
				mutation CreateClaimedItem(
					$policyUid: String!,
					$damageValue: Float!,
					$damageDescription: String!,
					$replacementValue: Float,
					$files: [FileInput]
				) {
					createClaimedItem(
						policyUid: $policyUid,
						damageValue: $damageValue,
						damageDescription: $damageDescription,
						replacementValue: $replacementValue,
						files: $files
					) {
						id
						title
					}
				}
			`,
			{
				policyUid: props.policyUid,
				damageValue: parseFloat(formData.damageValue),
				damageDescription: formData.damageDescription,
				replacementValue: formData.replacementValue
					? parseFloat(formData.replacementValue)
					: null,
				files: formData.uploadedFiles,
			},
		);

		if (json.errors) {
			// Handle GraphQL errors
			const errorMessage =
				json.errors[0]?.message || "An error occurred while creating the claim";
			console.error("GraphQL error:", errorMessage);
			generalError.value = errorMessage;
			return;
		}

		// Handle successful submission - redirect to claims list
		window.location.href =
			"/expeditions/policies/" + props.policyUid + "/claim/list";
	} catch (error) {
		console.error("Form submission error:", error);
		generalError.value = "A network error occurred. Please try again.";
	} finally {
		isSubmitting.value = false;
	}
};

/**
 * Converts a file to base64 data URL
 */
const fileToBase64 = (file) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
};

/**
 * Handles file upload updates
 */
const handleFileUpload = async (files) => {
	// Convert files to the format expected by GraphQL
	const convertedFiles = [];

	for (const file of files) {
		const fileData = await fileToBase64(file);
		convertedFiles.push({
			fileData: fileData,
			filename: file.name,
		});
	}

	formData.uploadedFiles = convertedFiles;
};

/**
 * Navigates back to the previous page
 */
const goBack = () => {
	window.location.href =
		props.backUrl || "/expeditions/policies/" + props.policyUid;
};
</script>

<template>
	<div class="shipment complete">
		<div class="shipment-body">
			<form ref="formRef" @submit="handleSubmit">
				<!-- General error message -->
				<div v-if="generalError" class="alert alert-danger mb-3">
					{{ generalError }}
				</div>

				<fieldset>
					<legend>Damage</legend>
					<div class="row">
						<div class="col-12 col-lg-6 mb-3">
							<label class="form-label">
								Damage value<span class="required">*</span>
							</label>
							<div class="input-group">
								<input
									v-model="formData.damageValue"
									type="number"
									class="form-control"
									:class="{ 'is-invalid': errors.damageValue }"
									name="damageValue"
									placeholder="500,00"
									min="0"
									step="0.01"
								/>
								<div class="input-group-append">
									<div class="input-group-text">{{ currencySymbol }}</div>
								</div>
							</div>
							<div v-if="errors.damageValue" class="invalid-feedback">
								{{ errors.damageValue }}
							</div>
						</div>
						<div class="col-12 col-lg-6 mb-3">
							<label class="form-label"> Replacement value </label>
							<div class="input-group">
								<input
									v-model="formData.replacementValue"
									type="number"
									class="form-control"
									:class="{ 'is-invalid': errors.replacementValue }"
									name="replacementValue"
									placeholder="1.000,00"
									min="1"
									step="0.01"
								/>
								<div class="input-group-append">
									<div class="input-group-text">{{ currencySymbol }}</div>
								</div>
							</div>
							<div v-if="errors.replacementValue" class="invalid-feedback">
								{{ errors.replacementValue }}
							</div>
						</div>
						<div class="col-12 mb-3">
							<label class="form-label">
								Damage description<span class="required">*</span>
							</label>
							<textarea
								v-model="formData.damageDescription"
								name="damageDescription"
								rows="4"
								class="form-control"
								:class="{ 'is-invalid': errors.damageDescription }"
								placeholder="Please describe as detailed as possible what is wrong with your item and if you have an idea of the cause of damage please let us know."
							></textarea>
							<div v-if="errors.damageDescription" class="invalid-feedback">
								{{ errors.damageDescription }}
							</div>
						</div>
					</div>
				</fieldset>

				<fieldset class="mb-4">
					<legend>Documents and photos</legend>
					<div class="small mb-3">
						If you have pictures of the object in its original condition and/or
						damaged condition please upload them here.
					</div>

					<FileUploader
						v-model="formData.uploadedFiles"
						:max-files="10"
						:max-size="10485760"
						label="Click to add photos and documents to continue."
						:accepted-extensions="[
							'.jpg',
							'.jpeg',
							'.png',
							'.pdf',
							'.doc',
							'.docx',
						]"
						@update:modelValue="handleFileUpload"
					/>
				</fieldset>

				<div class="d-flex align-items-center justify-content-between">
					<button
						type="button"
						@click="goBack"
						class="btn btn-rounded btn-outline-blue"
						:disabled="isSubmitting"
					>
						<i class="ri-arrow-left-line"></i>
						<span>Back</span>
					</button>

					<button
						type="submit"
						class="btn btn-rounded btn-blue"
						:disabled="isSubmitting"
					>
						<span>{{ isSubmitting ? "Saving..." : "Save" }}</span>
						<i class="ri-arrow-right-line"></i>
					</button>
				</div>
			</form>
		</div>
	</div>
</template>
